import { auth, onAuthStateChanged } from './firebase';
import { User } from 'firebase/auth';
import {
  BrdTemplate,
  BrdTemplatePayload,
  BrdTemplatesResponse,
  BrdTemplateSingleResponse
} from '../types';

/**
 * Cloud Run base URL for the BRD Template API.
 * Reads from environment variables (CRA: REACT_APP_BRD_API_URL, Vite: VITE_BRD_API_URL),
 * falling back to the deployed Cloud Run endpoint.
 */
export const BRD_API_BASE_URL = (
  process.env.REACT_APP_BRD_API_URL ||
  process.env.VITE_BRD_API_URL ||
  'https://brd-template-api-362149611676.asia-south1.run.app'
).replace(/\/+$/, '');

/**
 * Standardized Error class for BRD API interactions.
 */
export class BrdApiError extends Error {
  status: number;
  data?: any;

  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = 'BrdApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Waits for Firebase auth to initialize and resolves the active Firebase User.
 */
export const waitForFirebaseUser = (timeoutMs = 1500): Promise<User | null> => {
  if (auth.currentUser) {
    return Promise.resolve(auth.currentUser);
  }

  return new Promise<User | null>((resolve) => {
    let resolved = false;
    let unsub: (() => void) | undefined;

    const timeoutId = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        if (typeof unsub === 'function') {
          try {
            unsub();
          } catch {
            // ignore
          }
        }
        resolve(auth.currentUser || null);
      }
    }, timeoutMs);

    unsub = onAuthStateChanged(auth, (user) => {
      if (!resolved) {
        resolved = true;
        clearTimeout(timeoutId);
        if (typeof unsub === 'function') {
          try {
            unsub();
          } catch {
            // ignore
          }
        }
        resolve(user);
      }
    });
  });
};

/**
 * Obtains a fresh Firebase ID token for authenticated requests.
 * Uses user.getIdToken() rather than passing a Google OAuth access token.
 */
export const getFirebaseIdToken = async (forceRefresh = false): Promise<string> => {
  const user = await waitForFirebaseUser();
  if (!user) {
    throw new BrdApiError('Authentication required. Please sign in to access BRD templates.', 401);
  }

  const token = await user.getIdToken(forceRefresh);
  if (!token) {
    throw new BrdApiError('No active authentication token available. Please sign in again.', 401);
  }

  return token;
};

/**
 * Internal helper to format API error messages without leaking internal server stack traces.
 */
const parseErrorMessage = (status: number, responseData?: any): string => {
  const serverMsg = responseData?.error || responseData?.message;

  switch (status) {
    case 400:
      return serverMsg || 'Validation error: please verify your template inputs.';
    case 401:
      return serverMsg || 'Your session has expired or is invalid. Please sign in again.';
    case 403:
      return serverMsg || 'Access denied: you do not have permission for this template.';
    case 404:
      return serverMsg || 'The requested template was not found or access is denied.';
    default:
      if (status >= 500) {
        return 'A server error occurred. Please try again later.';
      }
      return serverMsg || `Request failed with status ${status}.`;
  }
};

/**
 * Centralized authenticated fetch handler for the BRD template API.
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  retryOn401 = true
): Promise<T> {
  const token = await getFirebaseIdToken();
  const url = `${BRD_API_BASE_URL}${endpoint}`;

  const headers: Record<string, string> = {
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
    ...((options.headers as Record<string, string>) || {})
  };

  if (options.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      headers
    });
  } catch (err: any) {
    throw new BrdApiError(
      'Network connection error: unable to reach the BRD template service.',
      0,
      err
    );
  }

  // Handle token refresh on 401
  if (response.status === 401 && retryOn401) {
    try {
      const freshToken = await getFirebaseIdToken(true);
      headers.Authorization = `Bearer ${freshToken}`;
      response = await fetch(url, {
        ...options,
        headers
      });
    } catch {
      // Fall through to error handler below
    }
  }

  if (response.ok) {
    if (response.status === 204) {
      return {} as T;
    }
    const text = await response.text();
    if (!text) {
      return {} as T;
    }
    try {
      return JSON.parse(text) as T;
    } catch {
      return text as unknown as T;
    }
  }

  // Error handling
  let parsedErrorData: any = null;
  try {
    parsedErrorData = await response.json();
  } catch {
    // Response is not JSON
  }

  const message = parseErrorMessage(response.status, parsedErrorData);
  throw new BrdApiError(message, response.status, parsedErrorData);
}

/**
 * Lists all BRD templates owned by or shared with the authenticated user.
 * GET /brd-templates
 */
export const listBrdTemplates = async (): Promise<BrdTemplate[]> => {
  const data = await apiRequest<BrdTemplatesResponse>('/brd-templates', {
    method: 'GET'
  });
  return Array.isArray(data?.templates) ? data.templates : [];
};

/**
 * Fetches a single BRD template by ID.
 * GET /brd-templates/{brd_id}
 */
export const getBrdTemplate = async (brdId: string): Promise<BrdTemplate> => {
  if (!brdId) {
    throw new BrdApiError('Template ID is required', 400);
  }
  const data = await apiRequest<BrdTemplateSingleResponse>(
    `/brd-templates/${encodeURIComponent(brdId)}`,
    {
      method: 'GET'
    }
  );
  if (!data?.template) {
    throw new BrdApiError('Template not found', 404);
  }
  return data.template;
};

/**
 * Creates a new BRD template.
 * POST /brd-templates
 */
export const createBrdTemplate = async (
  payload: BrdTemplatePayload
): Promise<BrdTemplate | any> => {
  if (!payload?.brd_template_name || !payload.brd_template_name.trim()) {
    throw new BrdApiError('Template name cannot be empty', 400);
  }

  return apiRequest<BrdTemplate | any>('/brd-templates', {
    method: 'POST',
    body: JSON.stringify({
      brd_template_name: payload.brd_template_name.trim(),
      brd_template_readme: payload.brd_template_readme || ''
    })
  });
};

/**
 * Updates an existing BRD template (owner only).
 * PUT /brd-templates/{brd_id}
 */
export const updateBrdTemplate = async (
  brdId: string,
  payload: BrdTemplatePayload
): Promise<BrdTemplate | any> => {
  if (!brdId) {
    throw new BrdApiError('Template ID is required', 400);
  }
  if (!payload?.brd_template_name || !payload.brd_template_name.trim()) {
    throw new BrdApiError('Template name cannot be empty', 400);
  }

  return apiRequest<BrdTemplate | any>(`/brd-templates/${encodeURIComponent(brdId)}`, {
    method: 'PUT',
    body: JSON.stringify({
      brd_template_name: payload.brd_template_name.trim(),
      brd_template_readme: payload.brd_template_readme || ''
    })
  });
};

/**
 * Deletes a BRD template (owner only).
 * DELETE /brd-templates/{brd_id}
 */
export const deleteBrdTemplate = async (brdId: string): Promise<void> => {
  if (!brdId) {
    throw new BrdApiError('Template ID is required', 400);
  }

  await apiRequest<void>(`/brd-templates/${encodeURIComponent(brdId)}`, {
    method: 'DELETE'
  });
};

/**
 * Shares a BRD template with a user.
 * POST /brd-templates/share
 * Request body: { "brd_id": string, "user_email": string }
 */
export const shareBrdTemplate = async (payload: {
  brd_id: string;
  user_email: string;
}): Promise<any> => {
  if (!payload?.brd_id) {
    throw new BrdApiError('Template ID is required', 400);
  }
  if (!payload?.user_email || !payload.user_email.trim() || !payload.user_email.includes('@')) {
    throw new BrdApiError('A valid user email is required to share the template.', 400);
  }

  return apiRequest<any>('/brd-templates/share', {
    method: 'POST',
    body: JSON.stringify({
      brd_id: payload.brd_id,
      user_email: payload.user_email.trim().toLowerCase()
    })
  });
};

/**
 * Diagnostic health check endpoint (does not require auth).
 * GET /health
 */
export const checkBrdApiHealth = async (): Promise<{ status: string }> => {
  const url = `${BRD_API_BASE_URL}/health`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' }
  });

  if (!response.ok) {
    throw new BrdApiError('Health check failed', response.status);
  }

  try {
    return await response.json();
  } catch {
    return { status: 'ok' };
  }
};

/**
 * Helper to determine if the given template is owned by the user.
 * Normalizes email casing for comparison.
 */
export const isTemplateOwner = (template: BrdTemplate, userEmail?: string | null): boolean => {
  if (!userEmail || !template?.brd_owner_email) {
    return false;
  }
  return template.brd_owner_email.trim().toLowerCase() === userEmail.trim().toLowerCase();
};

/**
 * Helper to format creation date string into friendly readable format.
 */
export const formatTemplateDate = (dateStr?: string): string => {
  if (!dateStr) return 'Recently';
  try {
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  } catch {
    // fallback
  }
  return dateStr;
};
